import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, FormError, PageHeader } from "@/components/common/ui-bits";
import { PhotoThumb } from "@/components/scan/Scan";
import { api, errMsg } from "@/lib/api";
import { todayNassau } from "@/lib/format";

const FIELDS = [["vendor", "Vendor", "text"], ["amount", "Amount", "text"], ["date", "Date paid / bill date", "date"], ["bill_number", "Bill #", "text"],
  ["account_number", "Account #", "text"], ["due_date", "Due date", "date"], ["period_start", "Period start", "date"], ["period_end", "Period end", "date"],
  ["description", "Description", "text"]];

function Item({ s, cats, onDone }) {
  const f0 = s.fields || {};
  const [form, setForm] = useState({ ...f0, date: f0.date || f0.bill_date || todayNassau(), category_id: f0.category_id || "", payment_method: f0.payment_method || "cash" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setForm((x) => ({ ...x, [k]: e.target.value })); setError(""); };
  const amount = String(form.amount || "").replace(/[,$]/g, "");
  const approve = async (as) => {
    const fields = as === "bill"
      ? { category_id: form.category_id, vendor: form.vendor, bill_number: form.bill_number, account_number: form.account_number, amount,
        period_start: form.period_start || null, period_end: form.period_end || null, due_date: form.due_date || null, notes: form.description || "" }
      : { date: form.date, amount, category_id: form.category_id, vendor: form.vendor, description: form.description || "", payment_method: form.payment_method };
    setBusy(true);
    try { await api.post(`/scans/${s.id}/approve`, { as, fields }); toast.success(as === "bill" ? "Approved as a bill" : "Approved as an expense"); onDone(); }
    catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };
  const reject = async () => { await api.post(`/scans/${s.id}/reject`); toast.success("Scan rejected"); onDone(); };
  return (
    <li className="surface p-4 sm:p-5 space-y-3 fade-up" data-testid={`review-item-${s.id}`}>
      <div className="flex items-center gap-3">
        <PhotoThumb photoId={s.photo_id} size="h-20 w-20" testId={`review-photo-${s.id}`} />
        <div className="min-w-0"><div className="font-semibold">From {s.user_name}</div>
          <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("en-US", { timeZone: "America/Nassau" })}</div></div>
      </div>
      <div>
        <div className="text-xs mb-1.5">Category</div>
        <div className="flex flex-wrap gap-1.5">
          {cats.map((c) => (
            <button key={c.id} type="button" onClick={() => setForm((x) => ({ ...x, category_id: c.id }))} aria-pressed={form.category_id === c.id}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium ${form.category_id === c.id ? "bg-primary border-primary" : "bg-card border-border"}`} data-testid={`review-category-${s.id}-${c.id}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className={k === "description" || k === "vendor" ? "col-span-2" : ""}>
            <Label className="text-xs" htmlFor={`rv-${s.id}-${k}`}>{label}</Label>
            <Input id={`rv-${s.id}-${k}`} type={type} inputMode={k === "amount" ? "decimal" : undefined} value={form[k] || ""} onChange={set(k)} className="mt-1 h-10 rounded-xl" data-testid={`review-${k.replace(/_/g, "-")}-${s.id}`} />
          </div>
        ))}
      </div>
      <FormError message={error} testId={`review-error-${s.id}`} />
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => approve("bill")} className="rounded-xl h-11 flex-1 min-w-[9rem]" data-testid={`review-approve-bill-${s.id}`}>Approve as Bill</Button>
        <Button disabled={busy} onClick={() => approve("expense")} variant="outline" className="rounded-xl h-11 flex-1 min-w-[9rem]" data-testid={`review-approve-expense-${s.id}`}>Approve as Expense</Button>
        <Button disabled={busy} onClick={reject} variant="ghost" className="rounded-xl h-11 text-rose-700" data-testid={`review-reject-${s.id}`}>Reject</Button>
      </div>
    </li>
  );
}

function History({ items }) {
  if (!items.length) return null;
  return (
    <section className="mt-10" data-testid="review-history">
      <h2 className="font-heading text-base md:text-lg font-semibold mb-3">Recently reviewed</h2>
      <ul className="surface divide-y divide-border">
        {items.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`review-history-item-${s.id}`}>
            <span className="min-w-0 truncate"><span className="font-semibold">{s.fields?.vendor || "Unknown vendor"}</span> · {s.user_name}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.status === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-700"}`}>
              {s.status === "approved" ? "Approved" : "Rejected"}{s.reject_reason ? ` · ${s.reject_reason}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ReviewPage() {
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [history, setHistory] = useState([]);
  const load = useCallback(() => {
    api.get("/scans/pending").then((r) => setData(r.data));
    api.get("/scans/reviewed").then((r) => setHistory(r.data));
  }, []);
  useEffect(() => { load(); api.get("/categories/active", { params: { type: "expense" } }).then((r) => setCats(r.data.filter((c) => c.type === "expense"))); }, [load]);
  return (
    <div className="max-w-2xl">
      <PageHeader eyebrow="Staff scans" title="Pending review" subtitle="Not counted anywhere until you approve them." testId="review-title" />
      <p className="text-sm font-semibold mb-3" data-testid="review-count">{data ? `${data.count} waiting` : "Loading…"}</p>
      <ul className="space-y-4" data-testid="review-list">
        {data?.count === 0 && <div className="surface"><EmptyState icon={Inbox} text="Nothing waiting for review" testId="review-empty" /></div>}
        {data?.items.map((s) => <Item key={s.id} s={s} cats={cats} onDone={() => { load(); window.dispatchEvent(new Event("dls-review-changed")); }} />)}
      </ul>
      <History items={history} />
    </div>
  );
}
